"use client";

import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";

import ArrowNavigationSkeleton from "@/components/ui/loaders/ArrowNavigationSkeleton";
import ArrowNavigation from "@/components/ui/pagination/ArrowNavigation";
import { useRouter } from "@/i18n/routing";

interface ArticlesPaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage?: number;
  isLoading?: boolean;
}

const ArticlesPagination = ({
  currentPage,
  totalItems,
  itemsPerPage = 20,
  isLoading = false,
}: ArticlesPaginationProps) => {
  const router = useRouter();
  const locale = useLocale();
  const searchParams = useSearchParams();

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());

    if (newPage === 1) {
      params.delete("page");
    } else {
      params.set("page", newPage.toString());
    }

    const queryString = params.toString();
    const baseUrl = locale === "en" ? "/articles" : `/${locale}/articles`;
    const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    router.push(newUrl);
  };

  if (isLoading) {
    return <ArrowNavigationSkeleton className="justify-center py-8" />;
  }

  if (totalItems === 0) {
    return null;
  }

  return (
    <nav dir="ltr" className="flex justify-center py-8" aria-label="Articles Pagination" role="navigation">
      <ArrowNavigation
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        ariaLabel="Articles pagination navigation"
      />
    </nav>
  );
};

export default ArticlesPagination;
